import { ArrowLeft } from 'lucide-react';
import OrcaLogo from './OrcaLogo';

const DOCS = {
  legal: {
    title: 'Legal',
    updated: '21 August 2026',
    sections: [
      {
        heading: 'About this service',
        body: 'Orca AI Reports is an independent application that helps authorized users find, compose, and run reports against their own Oracle Fusion Cloud HCM tenant. It is not an Oracle product, and Oracle does not sponsor or endorse this application unless a separate partner listing says otherwise.',
      },
      {
        heading: 'Customer responsibility',
        body: 'You are responsible for the Fusion credentials you store, the people you invite, and the queries or reports you run. Use an integration user with the least privilege required. Do not use this tool to access a tenant you are not authorized to use.',
      },
      {
        heading: 'Oracle trademarks',
        body: 'Oracle, Java, MySQL, and NetSuite are registered trademarks of Oracle and/or its affiliates. Other names may be trademarks of their respective owners. References to Oracle Cloud HCM, BI Publisher, and OTBI describe interoperability with those products.',
      },
      {
        heading: 'No warranty',
        body: 'The service is provided as-is. Generated SQL, report matches, and parameter suggestions can be incomplete or incorrect. Always review results before you act on them in payroll, compensation, or other production processes.',
      },
      {
        heading: 'Contact',
        body: 'For legal notices about this application, contact the operator who provisioned your Orca AI workspace.',
      },
    ],
  },
  privacy: {
    title: 'Privacy',
    updated: '21 August 2026',
    sections: [
      {
        heading: 'What this app stores',
        body: 'Orca AI Reports stores workspace accounts, session cookies, encrypted Fusion connection credentials, optional saved queries, and an audit log of copilot prompts and responses needed to operate the product.',
      },
      {
        heading: 'Where HCM data lives',
        body: 'Employee, payroll, absence, and other HCM records are queried from the Fusion tenant you activate. Those records are shown in your browser for the current session so you can preview or export them. They are not copied into a separate HCM database inside Orca.',
      },
      {
        heading: 'LLM processing',
        body: 'Natural-language questions and generated SQL may be sent to the configured language-model provider (for example OCI Generative AI or DeepSeek) so the copilot can compose a query. Do not paste secrets or unnecessary personal data into prompts.',
      },
      {
        heading: 'Cookies and sessions',
        body: 'A signed session cookie keeps you signed in. If WorkOS SSO is enabled, that provider also processes sign-in according to its own terms.',
      },
      {
        heading: 'Your choices',
        body: 'You can remove saved connections, sign out, and ask a workspace admin to delete your account. Export files you download (CSV, Excel, PDF) are under your control once they leave the app.',
      },
    ],
  },
};

export default function LegalPrivacy({ docId, onBack }) {
  const doc = DOCS[docId] || DOCS.legal;

  return (
    <div className="min-h-screen bg-orca-base px-4 py-10 text-orca-text">
      <div className="mx-auto w-full max-w-2xl rounded-2xl border border-orca bg-orca-panel p-6 shadow-xl sm:p-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-orca-muted hover:text-orca-nav-hover"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to landing
        </button>
        <div className="mt-5 flex items-center gap-3">
          <OrcaLogo className="h-10 w-10" />
          <div>
            <h1 className="text-xl font-semibold text-orca-heading">{doc.title}</h1>
            <p className="text-xs text-orca-muted">Last updated {doc.updated}</p>
          </div>
        </div>
        <div className="mt-6 space-y-5">
          {doc.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-sm font-semibold text-orca-heading">{section.heading}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-orca-muted">{section.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
